import { AnyElement, MuWidget } from "mu-widget/lib/MuWidget";

import { FormatList, MessagePath } from "./Types";
import { UiFlashContainer, UiFlashItem } from "./Ui/UiFlash";

export class BaseWidget extends MuWidget // IMuWidget
{
    protected formats: FormatList = {
        NoticeNumber: /^(19|20)\d{2}\/S \d{3}-\d{6}$/,
        Date: /^(0[1-9]|1[0-9]|2[0-9]|3[0-1])\.(0[1-9]|1[0-2])\.\d{4}$/,
        ESenderLogin: /^[a-zA-Z]{1}[a-zA-Z0-9]{4,7}$/,
        CustomerLogin: /^[A-Za-z0-9._\-]{1,30}$/,
        NoDocExt: /^20\d{2}\-\d{6}$/,
        Phone: /(\+\d{1,3}\s\d+(\-\d+)*((\s)?\/(\s)?(\+\d{1,3}\s)?\d+(\-\d+)*)*)/,
        Email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
    }

    public visibleByCheck(ev: any/* MuEventWrapper */, target: string, neg: boolean = false) {
        let vis = (ev.source as HTMLInputElement).checked;
        if (neg) vis = !vis;
        this.muVisible(vis, target);
    }

    addFlash(message: string, messagClass: string | null | "error" | "saved" | "deleted" = null): UiFlashItem {
        const fm = UiFlashContainer.add(message);

        if (messagClass) fm.container.classList.add('flash-message__flash--' + messagClass);

        return fm;
    }

    override afterIndex() {
        this.updateForm();
        this.updateSubWidgets();
    }

    override muAfterBindData() {
        this.updateForm();
        this.updateSubWidgets();
    }

    updateSubWidgets() {
        for (const w of this.muSubWidgets) {
            if (w["updateForm"]) (w as any).updateForm();
        }
    }

    public updateForm() { }

    public findElementByPath(path: MessagePath): AnyElement | null {
        if (!this.muBindOpts[path[0]]) return null;
        const el = this.muBindOpts[path[0]][0].element;
        if (path.length > 1) {
            return ((el as any).widget as BaseWidget).findElementByPath(path.slice(1));
        } else {
            return el;
        }
    }

    public getCoords(elem: HTMLElement) { // crossbrowser version
        var box = elem.getBoundingClientRect();

        var body = document.body;
        var docEl = document.documentElement;

        var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
        var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

        var clientTop = docEl.clientTop || body.clientTop || 0;
        var clientLeft = docEl.clientLeft || body.clientLeft || 0;

        var top = box.top + scrollTop - clientTop;
        var left = box.left + scrollLeft - clientLeft;

        return { top: Math.round(top), left: Math.round(left) };
    }

    public scrollToTargetAdjusted(element: HTMLElement) {
    var headerOffset = 45;
    var elementPosition = element.getBoundingClientRect().top;
    var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
    });
}

    /**
     * Function hides or shows the element based on return of a condition / boolean.
     * @param element - Element you want to hide
     * @param visibility - Hide (false) / Show (true)
     * @param disableMode - Disables the element instead of hiding it, if set to true
     * @param eraseValue - Erases values of child inputs' values, if set to true
     */
    public hideElement(element: HTMLElement, visibility: boolean, disableMode: boolean = false, eraseValue: boolean = false): void {
        const affectedClass: string = disableMode ? "disabled-section" : "hide-this"
        if (element !== null && element !== undefined) {
            visibility
                ? element.classList.remove(affectedClass)
                : (() => {
                    element.classList.add(affectedClass);

                    if (disableMode === true)
                        this.toggleDisabilityChildren(element, visibility ? "enable" : "disable");
                    if (eraseValue === true) {
                        const childInputs: Array<HTMLElement> = this.findChildElementsByTag(element, "input");
                        const childTextareas: Array<HTMLElement> = this.findChildElementsByTag(element, "textarea");

                        childInputs.forEach(e => {
                            this.resetCheckbox(e as HTMLInputElement);
                            this.resetText(e as HTMLInputElement);
                        });

                        childTextareas.forEach(e => this.resetText(e as HTMLTextAreaElement));
                    }
                })();
        }
    }

    /**
     * Function hides or shows elements found by given identification and its value based on return of a condition
     * @param idType - for instance "mu-id"
     * @param identifier - for instance "URL_block"
     * @param condition - dictates whether the element should be shown (true) or hidden (false)
     */
    hideElementsBy(idType: string, identifier: string, condition: boolean) {
        document.querySelectorAll('[' + idType + '="' + identifier + '"]').forEach(e => this.hideElement(e as HTMLElement, condition))
    }

    public toggleDisabilityChildren(element: HTMLElement, action: "enable" | "disable"): void {
        const childInputs: Array<HTMLElement> = this.findChildElementsByTag(element, "input");
        const childTextareas: Array<HTMLElement> = this.findChildElementsByTag(element, "textarea");

        childInputs.forEach(input => (input as HTMLInputElement).disabled = action === "disable");
        childTextareas.forEach(ta => (ta as HTMLTextAreaElement).disabled = action === "disable");
    }

    /**
     * Function finds the first element with given tag name in parents of an element. Returns null if there is none such element.
     * @param element - Element you want to find the parent of
     * @param tagName - Stringified name of the parent tag you want to find
     */
    public findTagElementParent(element: HTMLElement, tagName: string): HTMLElement | null {
        let currentElement = element.parentElement;

        while (currentElement) {
            if (currentElement.tagName.toLowerCase() === tagName) {
                return currentElement;
            }

            currentElement = currentElement.parentElement;
        }

        return null; // Section element not found
    }

    /**
     * Function finds all children of a parent element with given tag
     * @param parentElement
     * @param tagName
     */
    public findChildElementsByTag(parentElement: HTMLElement, tagName: string): HTMLElement[] {
        const elements: HTMLElement[] = [];

        const childElements = parentElement.getElementsByTagName(tagName);
        for (let i = 0; i < childElements.length; i++) {
            elements.push(childElements[i] as HTMLElement);
        }

        return elements;
    }

    /**
     * Function unchecks a checkbox
     * @param element - Checkbox you want to uncheck
     */
    public resetCheckbox(element: HTMLInputElement): void {
        if (element.type.toLowerCase() === "checkbox") {
            element.checked = false;
        }
    }

    /**
     * Function empties text/number input
     * @param element - Input you want to uncheck
     */
    public resetText(element: HTMLInputElement | HTMLTextAreaElement): void {
        if (element.type.toLowerCase() === "text" || element.type.toLowerCase() === "url" || element.type.toLowerCase() === "number" || element instanceof HTMLTextAreaElement === true) {
            element.value = "";
        }
    }

    /**
     * Function checks if element's value matches a given format, formats needs to be defined in this.formats, returns error message if check fails
     * @param element
     * @param format
     */
    public checkFormat(element: HTMLInputElement, format: string): true | string {
        if (this.formats.hasOwnProperty(format) === false) {
            return "Zadali jste neplatný formát pro kontrolu - " + format;
        }

        if (element.value == null)
            return true;

        return this.formats[format].test(element.value) || element.value.length === 0 ? true : "Text neodpovídá požadovanému formátu.";
    }

    /**
     * Function creates an element (hidden by default) with an error message that it returns for further use
     * @param el - Element you want to append the message to
     * @param forElement - Which element is the message related to
     * @param text - Text of the message
     */
    public createErrorMsg(el: HTMLElement, forElement: string, text: string): HTMLElement {
        const errorElement = document.createElement('div');
        errorElement.classList.add('error-list');
        errorElement.classList.add('hide-this');
        errorElement.setAttribute('mu-for', 'error:' + forElement);

        const errorItemElement = document.createElement('div');
        errorItemElement.classList.add('error-item');
        errorItemElement.textContent = text;
        errorElement.appendChild(errorItemElement);

        el.insertAdjacentElement('afterend', errorElement);

        return errorElement;
    }

    /**
     * Function adds an error outline and an exclamation mark with an optional tooltip to a given element
     * @param el - Element you want to add an error to
     * @param message - Error message to be shown in tooltip
     */
    public addErrorToElement(el: HTMLElement, message: string = ""): HTMLElement {
        el.classList.add("has-error");
        el.classList.add("is-invalid");
        const err = this.getError(el);

        if (err === null) {
            const warning = document.createElement('span');
            warning.classList.add("error-list");
            const warningMark = document.createElement('span');
            warningMark.textContent = '!';
            warningMark.classList.add("error-item");
            if (message.length !== 0) {
                warningMark.textContent = message;
            }

            warning.appendChild(warningMark);
            el.insertAdjacentElement('afterend', warning);

            return warning;
        } else {
            return err;
        }
    }

    /**
     * Function removes an error outline and an exclamation mark from a given element
     * @param el
     */
    public removeErrorFromElement(el: HTMLElement): void {
        el.classList.remove("has-error");
        el.classList.remove("is-invalid");

        const errMark: HTMLElement | null = this.getError(el);

        if (errMark !== null) {
            errMark.remove();
        }
    }

    /**
     * Function finds an error element related to a given element, returns null if not found
     * @param el
     */
    public getError(el: HTMLElement): HTMLElement | null {
        let sibling = el.nextElementSibling;

        while (sibling) {
            if (sibling instanceof HTMLElement && sibling.classList.contains("error-list")) {
                return sibling;
            }
            sibling = sibling.nextElementSibling;
        }

        return null;
    }

    /**
     * Function checks if an URL is valid or not and returns the result
     * @param url
     */
    public isValidURL(url: string): boolean {
        const urlValidations = [
            /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-.\/?%&=]*)?$/, // Pattern for http:// and https:// URLs
            /^([\w-]+\.)+[\w-]+(\/[\w-.\/?%&=]*)?$/ // Pattern for URLs without a scheme
        ];

        for (const validation of urlValidations) {
            if (validation.test(url)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Function checks if given CPV code is valid within given tender type
     * @param cpvCode
     * @param tenderType
     */
    public validateMainCPVCode(cpvCode: string, tenderType: string): boolean {
        const firstChars: string = cpvCode.substring(0, 2);

        switch (tenderType) {
            case "SUPPLIES":
                if (firstChars === "48") {
                    return true;
                } else if (firstChars >= "00" && firstChars <= "44") {
                    return true;
                }
                break;
            case "WORKS":
                if (firstChars === "45") {
                    return true;
                }
                break;
            case "SERVICES":
                if (firstChars >= "49" && firstChars <= "98") {
                    return true;
                }
                break;
        }

        return false;
    }


};